
import React, { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Search, Calendar, Download, FileDown, Eye } from "lucide-react";

// Sample data for the table
const sampleLedgerData = [
  {
    id: "M12345",
    name: "John Doe",
    phone: "012-345-6789",
    date: "2025-05-18",
    pointsEarned: 250,
    pointsRedeemed: 0,
    balance: 250,
    source: "POS",
    isAdjustment: false
  },
  {
    id: "M67890",
    name: "Jane Smith",
    phone: "012-987-6543",
    date: "2025-05-17",
    pointsEarned: 0,
    pointsRedeemed: 100,
    balance: 150,
    source: "Online",
    isAdjustment: false
  },
  {
    id: "M45678",
    name: "Ahmad Zakaria",
    phone: "019-876-5432",
    date: "2025-05-16",
    pointsEarned: 50,
    pointsRedeemed: 0,
    balance: 200,
    source: "Referral",
    isAdjustment: false
  },
  {
    id: "M78901",
    name: "Siti Rahman",
    phone: "017-765-4321",
    date: "2025-05-15",
    pointsEarned: 75,
    pointsRedeemed: 0,
    balance: 325,
    source: "POS",
    isAdjustment: false
  },
  {
    id: "M23456",
    name: "Raj Kumar",
    phone: "013-654-3210",
    date: "2025-05-14",
    pointsEarned: 30,
    pointsRedeemed: 0,
    balance: 180,
    source: "Manual",
    isAdjustment: true
  }
];

const PointsLedgerTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [transactionType, setTransactionType] = useState("all");
  const [filteredData, setFilteredData] = useState(sampleLedgerData);

  // Filter data based on search term and transaction type
  const handleSearch = () => {
    let filtered = sampleLedgerData;
    
    // Filter by search term (name or phone)
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.phone.includes(searchTerm) ||
        item.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by transaction type
    if (transactionType !== "all") {
      if (transactionType === "earned") {
        filtered = filtered.filter(item => item.pointsEarned > 0);
      } else if (transactionType === "redeemed") {
        filtered = filtered.filter(item => item.pointsRedeemed > 0);
      } else if (transactionType === "manual") {
        filtered = filtered.filter(item => item.isAdjustment);
      }
    }
    
    setFilteredData(filtered);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setTransactionType("all");
    setFilteredData(sampleLedgerData);
  };

  // View transaction details (placeholder function)
  const viewDetails = (id: string) => {
    console.log(`View details for transaction ${id}`);
    // This would open a modal or navigate to a details page in a real app
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Points Ledger</h2>
      
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Input 
                placeholder="Search by name, phone or ID..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleSearch} size="icon" variant="secondary">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Input type="date" className="flex-1" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" className="flex-1" />
            </div>
            
            <div className="flex items-center space-x-2">
              <Select value={transactionType} onValueChange={setTransactionType}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Transaction Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="earned">Points Earned</SelectItem>
                  <SelectItem value="redeemed">Points Redeemed</SelectItem>
                  <SelectItem value="manual">Manual Adjustments</SelectItem>
                </SelectContent>
              </Select>
              
              <Button onClick={handleSearch} variant="default" size="sm">
                Apply
              </Button>
              
              <Button onClick={resetFilters} variant="outline" size="sm">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Points Transaction History</CardTitle>
          <Button variant="outline" size="sm">
            <FileDown className="h-4 w-4 mr-2" /> Export
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>A list of all loyalty points transactions.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Member ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone Number</TableHead>
                <TableHead>Transaction Date</TableHead>
                <TableHead>Points Earned</TableHead>
                <TableHead>Points Redeemed</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((item) => (
                <TableRow key={`${item.id}-${item.date}`}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.phone}</TableCell>
                  <TableCell>{item.date}</TableCell>
                  <TableCell>
                    {item.pointsEarned > 0 ? (
                      <span className="text-green-600">+{item.pointsEarned}</span>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.pointsRedeemed > 0 ? (
                      <span className="text-red-600">-{item.pointsRedeemed}</span>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell>{item.balance}</TableCell>
                  <TableCell>
                    <Badge variant={
                      item.source === "POS" ? "default" :
                      item.source === "Online" ? "secondary" :
                      item.source === "Referral" ? "outline" :
                      "destructive"
                    }>
                      {item.source}
                      {item.isAdjustment && " (Admin)"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => viewDetails(`${item.id}-${item.date}`)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PointsLedgerTab;
